import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Check, X, Inbox, Send } from "lucide-react";

const STATUS = {
  requested: "bg-[#E68A2E]/15 text-[#E68A2E]",
  accepted: "bg-[#3B6B1E]/15 text-[#5FA83B]",
  declined: "bg-slate-400/15 text-slate-400",
};

export function TradeContactCard({ m }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/me/trade-contact").then((r) => setF({ ...r.data, email: r.data.email || r.data.account_email || "" })).catch(() => setF({ phone: "", email: "", share: false })); }, []);
  if (!f) return null;
  const save = async (share = f.share) => {
    setSaving(true);
    try { const { data } = await api.put("/me/trade-contact", { phone: f.phone, email: f.email, share }); setF((x) => ({ ...x, ...data })); toast.success(share ? "Contact saved — shared on accepted trades" : "Contact saved — not shared"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const inp = `w-full rounded-lg border px-3 py-2 text-sm bg-transparent ${m ? "border-white/10 text-slate-100" : "border-[#D7DBE0] text-[#14181F]"}`;
  return (
    <div className={`rounded-xl border p-4 ${m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]"}`} data-testid="trade-contact-card">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className={`font-display font-bold text-sm ${m ? "text-slate-100" : "text-[#14181F]"}`}>My trade contact</div>
        <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
          <input type="checkbox" checked={!!f.share} onChange={(e) => { setF({ ...f, share: e.target.checked }); save(e.target.checked); }} data-testid="trade-contact-share" className="accent-[#C8000F] w-4 h-4" />
          Share on accepted trades
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" data-testid="trade-contact-phone" className={inp} />
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="trade-contact-email" className={inp} />
      </div>
      <div className="flex items-center justify-between mt-2 gap-3">
        <p className="text-[11px] text-slate-400">Only the rider you've agreed a trade with sees this.</p>
        <button onClick={() => save()} disabled={saving} data-testid="trade-contact-save" className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#C8000F] text-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function ContactLine({ contact }) {
  if (!contact) return null;
  if (!contact.shared) return <div className="text-[11px] text-slate-400 mt-1" data-testid="trade-contact-hidden">They haven't shared contact details yet.</div>;
  return (
    <div className="flex flex-wrap gap-3 mt-1.5 text-xs font-semibold" data-testid="trade-contact-shown">
      {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-[#C8000F]"><Phone className="w-3.5 h-3.5" />{contact.phone}</a>}
      {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-[#C8000F]"><Mail className="w-3.5 h-3.5" />{contact.email}</a>}
    </div>
  );
}

export function TradeRequests({ m }) {
  const [inbox, setInbox] = useState(null);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => {
    api.get("/trades/inbox").then((r) => setInbox(r.data.requests)).catch(() => setInbox([]));
    api.get("/trades/requests").then((r) => setSent(r.data.requests)).catch(() => setSent([]));
  };
  useEffect(load, []);
  const respond = async (id, accept) => {
    setBusy(id);
    try { await api.post(`/trades/requests/${id}/respond`, { accept }); toast.success(accept ? "Trade accepted" : "Trade declined"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  if (!inbox || !sent) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>;
  const card = `rounded-xl border p-3 ${m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]"}`;
  const ttl = m ? "text-slate-100" : "text-[#14181F]";
  const head = (Icon, t, n) => <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${m ? "text-slate-500" : "text-slate-400"}`}><Icon className="w-3.5 h-3.5 text-[#C8000F]" /> {t} ({n})</div>;
  const pill = (s) => <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${STATUS[s] || STATUS.requested}`}>{s}</span>;
  return (
    <div className="space-y-4" data-testid="trade-requests">
      <div>
        {head(Inbox, "Requests to me", inbox.length)}
        {inbox.length === 0 ? <div className="text-xs text-slate-400">No one has asked you to trade yet.</div> : (
          <div className="space-y-2">{inbox.map((r) => (
            <div key={r.id} className={card} data-testid={`trade-inbox-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={`text-sm font-semibold ${ttl}`}>{r.requester_name || "A contestant"} wants your spot · {r.rodeo_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{r.event} · take their {r.my_performance_label}{r.my_performance_date ? ` (${fmtDate(r.my_performance_date)})` : ""}</div>
                  {r.message && <div className="text-xs mt-1 text-slate-400">“{r.message}”</div>}
                  <ContactLine contact={r.contact} />
                </div>
                {r.status === "requested" ? (
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => respond(r.id, true)} disabled={busy === r.id} data-testid={`trade-accept-${r.id}`} className="flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-[#3B6B1E] text-white disabled:opacity-50"><Check className="w-3.5 h-3.5" />Accept</button>
                    <button onClick={() => respond(r.id, false)} disabled={busy === r.id} data-testid={`trade-decline-${r.id}`} className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg ${m ? "bg-white/10 text-slate-300" : "bg-[#F1F3F5] text-slate-600"} disabled:opacity-50`}><X className="w-3.5 h-3.5" />Decline</button>
                  </div>
                ) : pill(r.status)}
              </div>
            </div>
          ))}</div>
        )}
      </div>
      <div>
        {head(Send, "Requests I sent", sent.length)}
        {sent.length === 0 ? <div className="text-xs text-slate-400">Tap Trade on a conflicted rodeo to find riders who can swap with you.</div> : (
          <div className="space-y-2">{sent.map((r) => (
            <div key={r.id} className={card} data-testid={`trade-sent-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={`text-sm font-semibold ${ttl}`}>{r.candidate_name} · {r.rodeo_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{r.event} · out of {r.my_performance_label || "my performance"}{!r.candidate_user_id ? " · not on Rodeo Pilot" : ""}</div>
                  <ContactLine contact={r.contact} />
                </div>
                {pill(r.status)}
              </div>
            </div>
          ))}</div>
        )}
      </div>
    </div>
  );
}
